import { useCallback } from "react";
import { client } from "@src/apolloClient";
import { BASEURL } from "@src/utils/api";
import {
  AbortMultipartUploadDocument,
  AbortMultipartUploadInput,
  AllowedPostMediaTypes,
  CompleteMultipartUploadDocument,
  CompleteMultipartUploadInput,
  CompleteMultipartUploadMutation,
  FileType,
  InitiateMultipartUploadDocument,
  InitiateMultipartUploadInput,
  InitiateMultipartUploadMutation,
  MultipartCompletedPartInput,
  MultipartUploadPartInput,
  UploadMultipartPartDocument,
  UploadMultipartPartMutation,
  useAbortMultipartUploadMutation,
  useCompleteMultipartUploadMutation,
  useInitiateMultipartUploadMutation,
  useUploadMultipartPartMutation,
} from "@src/gql/generated";
import { print } from "graphql";
import { MediaModule } from "@src/utils/mediaModule";
import { resolveUploadMimeType } from "@src/utils/helpers/resolveUploadMimeType";

export type MultipartUploadOptions = {
  file: File;
  fileType?: AllowedPostMediaTypes | FileType | string;
  onProgress?: (percent: number) => void;
  chunkSizeBytes?: number;
  /** Optional module tag persisted on Media when complete runs. */
  module?: MediaModule;
  /** Aborts the in-progress chunk loop and cleans up the multipart upload server-side. */
  signal?: AbortSignal;
};

export class UploadCancelledError extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "UploadCancelledError";
  }
}

export type MultipartUploadResult = {
  /** Persisted Media id (null if the backend predates it). */
  id?: string | null;
  fileUrl: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  size?: number | null;
  type?: string | null;
};

type InitiateMultipartUploadResponse = {
  uploadId: string;
  key: string;
  fileUrl?: string | null;
  partSize?: number | null;
  totalParts?: number | null;
};

type CompletedPart = {
  partNumber: number;
  eTag: string;
};

type MultipartMutationApi = {
  initiate: (
    input: InitiateMultipartUploadInput,
  ) => Promise<InitiateMultipartUploadResponse>;
  uploadPart: (
    input: MultipartUploadPartInput,
    signal?: AbortSignal,
  ) => Promise<CompletedPart>;
  complete: (
    input: CompleteMultipartUploadInput,
  ) => Promise<MultipartUploadResult>;
  abort: (input: AbortMultipartUploadInput) => Promise<boolean>;
};

const toFileType = (
  fileType?: AllowedPostMediaTypes | FileType | string,
): FileType => {
  const normalized = String(fileType || FileType.PostImage) as FileType;
  return normalized;
};

const parseUploadPartPayload = (responseText: string): CompletedPart => {
  const parsed = JSON.parse(responseText) as {
    data?: UploadMultipartPartMutation;
    errors?: Array<{ message?: string }>;
  };

  if (parsed.errors && parsed.errors.length > 0) {
    throw new Error(parsed.errors[0]?.message || "Chunk upload GraphQL error");
  }

  const payload = parsed.data?.uploadMultipartPart;
  if (!payload) {
    throw new Error("uploadMultipartPart returned empty data");
  }

  return {
    partNumber: payload.partNumber,
    eTag: payload.eTag,
  };
};

const uploadMultipartPartOverFormData = async (
  input: MultipartUploadPartInput,
  signal?: AbortSignal,
): Promise<CompletedPart> => {
  if (signal?.aborted) {
    throw new UploadCancelledError();
  }

  return new Promise((resolve, reject) => {
    const formData = new FormData();

    formData.append(
      "operations",
      JSON.stringify({
        query: print(UploadMultipartPartDocument),
        variables: {
          input: {
            uploadId: input.uploadId,
            key: input.key,
            partNumber: input.partNumber,
            chunk: null,
          },
        },
      }),
    );

    formData.append(
      "map",
      JSON.stringify({
        "0": ["variables.input.chunk"],
      }),
    );

    formData.append("0", input.chunk as Blob);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", BASEURL, true);
    xhr.withCredentials = true;

    const detachAbortListener = () => {
      signal?.removeEventListener("abort", onAbortSignal);
    };

    function onAbortSignal() {
      xhr.abort();
    }
    signal?.addEventListener("abort", onAbortSignal);

    xhr.onload = () => {
      detachAbortListener();
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new Error(
            `Chunk upload failed: ${xhr.status} ${xhr.statusText} - ${xhr.responseText}`,
          ),
        );
        return;
      }

      try {
        resolve(parseUploadPartPayload(xhr.responseText));
      } catch (error) {
        reject(error);
      }
    };

    xhr.onerror = () => {
      detachAbortListener();
      reject(new Error("Network error during chunk upload"));
    };

    xhr.onabort = () => {
      detachAbortListener();
      reject(new UploadCancelledError());
    };

    xhr.send(formData);
  });
};

const uploadLargeFileWithApi = async (
  api: MultipartMutationApi,
  { file, fileType, onProgress, chunkSizeBytes, module, signal }: MultipartUploadOptions,
): Promise<MultipartUploadResult> => {
  let uploadId = "";
  let key = "";
  const normalizedFileType = toFileType(fileType);

  const safeProgress = (value: number) => {
    if (onProgress) {
      onProgress(Math.max(0, Math.min(100, value)));
    }
  };

  const throwIfCancelled = () => {
    if (signal?.aborted) throw new UploadCancelledError();
  };

  const mimeType = resolveUploadMimeType(file);

  try {
    throwIfCancelled();

    const initiated = await api.initiate({
      filename: file.name,
      mimeType,
      fileSize: file.size,
      fileType: normalizedFileType,
    });

    uploadId = initiated.uploadId;
    key = initiated.key;

    if (!uploadId || !key) {
      throw new Error("Multipart upload could not be initiated");
    }

    const partSize =
      initiated.partSize && initiated.partSize > 0
        ? initiated.partSize
        : chunkSizeBytes && chunkSizeBytes > 0
          ? chunkSizeBytes
          : 5 * 1024 * 1024;

    const totalParts =
      initiated.totalParts && initiated.totalParts > 0
        ? initiated.totalParts
        : Math.max(1, Math.ceil(file.size / partSize));

    const completedParts: CompletedPart[] = [];
    let uploadedBytes = 0;

    safeProgress(1);

    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      throwIfCancelled();

      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunkBlob = file.slice(start, end);
      const chunkFile = new File(
        [chunkBlob],
        `${file.name}.part.${partNumber}`,
        {
          type: mimeType,
        },
      );

      const uploadedPart = await api.uploadPart(
        {
          uploadId,
          key,
          partNumber,
          chunk: chunkFile,
        },
        signal,
      );

      completedParts.push({
        partNumber: uploadedPart.partNumber,
        eTag: uploadedPart.eTag,
      });
      uploadedBytes += chunkBlob.size;

      const partProgress = Math.floor((uploadedBytes / file.size) * 100);
      safeProgress(Math.min(99, partProgress));
    }

    throwIfCancelled();

    const completeData = await api.complete({
      uploadId,
      key,
      fileSize: file.size,
      mimeType,
      fileType: normalizedFileType,
      parts: completedParts as MultipartCompletedPartInput[],
      ...(module ? { module: module as any } : {}),
    });

    safeProgress(100);

    return {
      id: completeData?.id ?? null,
      fileUrl: completeData?.fileUrl || initiated.fileUrl || null,
      fileName: completeData?.fileName || file.name,
      mimeType: completeData?.mimeType || mimeType,
      size: completeData?.size || file.size,
      type: completeData?.type || normalizedFileType,
    };
  } catch (error) {
    if (uploadId && key) {
      try {
        await api.abort({ uploadId, key });
      } catch {
        // Do not mask the original error with abort failures.
      }
    }

    throw error;
  }
};

const apolloMultipartApi: MultipartMutationApi = {
  initiate: async (input) => {
    const response = await client.mutate<InitiateMultipartUploadMutation>({
      mutation: InitiateMultipartUploadDocument,
      variables: { input },
      fetchPolicy: "no-cache",
    });

    const payload = response.data?.initiateMultipartUpload;
    if (!payload) {
      throw new Error("initiateMultipartUpload returned empty data");
    }

    return payload;
  },
  uploadPart: async (input, signal) => {
    return uploadMultipartPartOverFormData(input, signal);
  },
  complete: async (input) => {
    const response = await client.mutate<CompleteMultipartUploadMutation>({
      mutation: CompleteMultipartUploadDocument,
      variables: { input },
      fetchPolicy: "no-cache",
    });

    const payload = response.data?.completeMultipartUpload;
    if (!payload) {
      throw new Error("completeMultipartUpload returned empty data");
    }

    return {
      id: payload.id ?? null,
      fileUrl: payload.fileUrl || null,
      fileName: payload.fileName || null,
      mimeType: payload.mimeType || null,
      size: payload.size ?? null,
      type: payload.type || null,
    };
  },
  abort: async (input) => {
    const response = await client.mutate({
      mutation: AbortMultipartUploadDocument,
      variables: { input },
      fetchPolicy: "no-cache",
    });

    return Boolean(response.data?.abortMultipartUpload);
  },
};

export const uploadLargeFileInChunks = async (
  options: MultipartUploadOptions,
): Promise<MultipartUploadResult> =>
  uploadLargeFileWithApi(apolloMultipartApi, options);

export const useMultipartUpload = () => {
  const [initiateMultipartUploadMutation] =
    useInitiateMultipartUploadMutation();
  const [uploadMultipartPartMutation] = useUploadMultipartPartMutation();
  const [completeMultipartUploadMutation] =
    useCompleteMultipartUploadMutation();
  const [abortMultipartUploadMutation] = useAbortMultipartUploadMutation();

  const uploadWithHooks = useCallback(
    async (options: MultipartUploadOptions): Promise<MultipartUploadResult> => {
      const hookApi: MultipartMutationApi = {
        initiate: async (input) => {
          const response = await initiateMultipartUploadMutation({
            variables: { input },
          });

          const payload = response.data?.initiateMultipartUpload;
          if (!payload) {
            throw new Error("initiateMultipartUpload returned empty data");
          }
          return payload;
        },
        uploadPart: async (input, signal) => {
          return uploadMultipartPartOverFormData(input, signal);
        },
        complete: async (input) => {
          const response = await completeMultipartUploadMutation({
            variables: { input },
          });
          const payload = response.data?.completeMultipartUpload;
          if (!payload) {
            throw new Error("completeMultipartUpload returned empty data");
          }
          return {
            id: payload.id ?? null,
            fileUrl: payload.fileUrl || null,
            fileName: payload.fileName || null,
            mimeType: payload.mimeType || null,
            size: payload.size ?? null,
            type: payload.type || null,
          };
        },
        abort: async (input) => {
          const response = await abortMultipartUploadMutation({
            variables: { input },
          });
          return Boolean(response.data?.abortMultipartUpload);
        },
      };

      return uploadLargeFileWithApi(hookApi, options);
    },
    [
      abortMultipartUploadMutation,
      completeMultipartUploadMutation,
      initiateMultipartUploadMutation,
      uploadMultipartPartMutation,
    ],
  );

  return {
    uploadLargeFileInChunks: uploadWithHooks,
  };
};
